import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randNumber,
  randChanceBoolean,
  randRecentDate,
} from '@ngneat/falso'
import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { getExistingEntity } from './utils.js'
import { PatientHealthPlanItemTypeCodes } from '@/composables/patientHealthPlanItems.js'

export type PatientHealthPlanItemData =
  SchemaAttributes<'PatientHealthPlanItem'>

export const model = (
  Model as ModelDefinition<PatientHealthPlanItemData>
).extend({
  item: belongsTo('organizationItem'),
  healthPlan: belongsTo('patientHealthPlan'),
})

export const factory = Factory.extend<PatientHealthPlanItemData>({
  type_code() {
    return rand(Object.values(PatientHealthPlanItemTypeCodes))
  },
  used() {
    return randChanceBoolean({ chanceTrue: 0.8 })
  },
  used_time() {
    return this.used ? randRecentDate({ days: 30 }).toISOString() : null
  },
  original_quantity() {
    return randNumber({ min: 1, max: 100, precision: 0.01 })
  },
  per_consultation() {
    return randNumber({ min: 0.5, max: 20, precision: 0.01 })
  },
  use_after_expiry() {
    return randChanceBoolean({ chanceTrue: 0.2 })
  },
  days_after_expiry() {
    return this.use_after_expiry ? randNumber({ min: 1, max: 365 }) : null
  },

  afterCreate(patientHealthPlanItem, server) {
    if (patientHealthPlanItem.used && !patientHealthPlanItem.item) {
      const item = getExistingEntity(server, 'organizationItem')
      patientHealthPlanItem.update({ item })
    }

    if (!patientHealthPlanItem.healthPlan) {
      const healthPlan = getExistingEntity(server, 'patientHealthPlan')
      patientHealthPlanItem.update({ healthPlan })
    }
  },
})

export function seeds(server: MockServer) {
  server.createList('patientHealthPlanItem', 50)
}

export function routes(server: MockServer) {
  server.get(
    '/patient_health_plan_items',
    function (this: MirageServer, schema) {
      return schema.all('patientHealthPlanItem')
    },
  )
}
